import { cloneElement, ElementType, forwardRef, HTMLProps, PropsWithChildren, ReactElement, useState } from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs
} from "@floating-ui/react";
import { IconX } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import "./modal.scss";

type ValidChildren = [ ReactElement<any, typeof ModalTrigger>, ReactElement<any, typeof ModalContent> ] |
  [ ReactElement<any, typeof ModalTrigger>, ReactElement<any, typeof ModalContent>, ReactElement<any, typeof ModalFooter> ];

type InnerProps = HTMLProps<HTMLElement> & { as?: ElementType };
type ContentProps = InnerProps & { title?: string };

type Props = {
  size?: "sm" | "md" | "lg";
  closableCross?: boolean;
  children: ValidChildren;
}

type ExternalOpenState = {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const ModalTrigger = forwardRef<HTMLElement, InnerProps>(({ as: Tag = "div", ...rest }, forwardRef) =>
  <Tag ref={forwardRef} {...rest} />);

export const ModalContent = forwardRef<HTMLElement, InnerProps>(({ as: Tag = "div", ...rest }, forwardRef) =>
  <Tag re={forwardRef} {...rest} />);

export const ModalFooter = ({ children }: PropsWithChildren) => children;

const Modal = (
  {
    size = "md",
    closableCross = true,
    children,
    open: externalOpen,
    setOpen: externalSetOpen
  }: Props & Partial<ExternalOpenState>) => {

  const [ open, setOpen ] = (typeof externalOpen === "boolean" && !!externalSetOpen)
    ? [ externalOpen, externalSetOpen ]
    : useState<boolean>(false);

  const { context, refs } = useFloating({
    open,
    onOpenChange: setOpen,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      referencePressEvent: "click",
      outsidePressEvent: "click",
      outsidePress: e => !(e.target as HTMLElement).classList.contains("to-modal"),
      ...(!closableCross) && { escapeKey: false, referencePress: false, outsidePress: false }
    })
  ]);

  const [ trigger, content, footer ] = children;
  const wrapperClasses = classNames("modal-content", size);
  const bodyClasses = classNames("modal-body", content.props.className);

  return (
    <>
      {cloneElement(trigger, {
        ref: useMergeRefs([refs.setReference, trigger.props.ref]),
        ...trigger.props,
        ...getReferenceProps(trigger.props)
      })}
      {open &&
        <FloatingPortal id="modal">
          <FloatingOverlay className="modal-overlay" lockScroll />
          <FloatingFocusManager context={context}
                                initialFocus={-1}>
            <section className={wrapperClasses}
                 ref={refs.setFloating}
                 {...getFloatingProps()}>
              {!!closableCross &&
                <IconX className="c-icon" onClick={() => setOpen(false)} />
              }
              {!!content.props.title &&
                <div className="modal-header">
                  <h1>{content.props.title}</h1>
                </div>
              }
              {cloneElement(content, { ...content.props, className: bodyClasses })}
              {!!footer &&
                <div className="modal-footer">{footer}</div>
              }
            </section>
          </FloatingFocusManager>
        </FloatingPortal>
      }
    </>);

}

export default Modal;
